import { useCallback, useEffect, useRef, useState } from "react";
import type { FireflyAskHighlightRequest } from "@/components/activity/ActivityDockAskFirefly";
import { useFireflyTourSession } from "@/components/activity/firefly/useFireflyTourSession";
import { useActivityDockAssistLevel } from "@/components/activity/useActivityDockAssistLevel";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { evaluateFireflyDiscoveryOffer } from "@/lib/firefly/evaluateFireflyDiscoveryOffer";
import { getFireflyDiscoveryConfigForMode } from "@/lib/firefly/fireflyDiscoveryConfig";
import { getFireflyTourById } from "@/lib/firefly/tourCatalog";
import {
  isFireflyDiscoveryOfferDismissed,
  isFireflyTourCompleted,
} from "@/lib/firefly/fireflyTourDiscoveryStorage";
import { registerFireflyTourStarter } from "@/lib/firefly/fireflyTourStartBridge";
import type { FireflyTourArtifact, FireflyTourTrigger } from "@/lib/firefly/tourTypes";
import type { LeadWorkspaceShellModel } from "@/components/lead/useLeadWorkspaceShellProps";

export function useLeadWorkspaceShellFirefly(args: {
  mode: PrimaryMode;
  auth: LeadWorkspaceShellModel["auth"];
  guardedSetMode: (mode: PrimaryMode) => void;
  handleOpenAdsAssistant: () => void;
}) {
  const { mode, auth, guardedSetMode, handleOpenAdsAssistant } = args;

  const [fireflyAskHighlight, setFireflyAskHighlight] = useState<FireflyAskHighlightRequest | null>(
    null
  );
  const { level: assistLevel } = useActivityDockAssistLevel();
  const hasCapability = useUserCapabilities();
  const [discoveryDismissedSession, setDiscoveryDismissedSession] = useState<Set<string>>(
    () => new Set()
  );
  const [discoveryDwellReady, setDiscoveryDwellReady] = useState(false);
  const modeEnteredAtRef = useRef(Date.now());
  const discoveryConfig = getFireflyDiscoveryConfigForMode(mode);
  const discoveryArtifact = discoveryConfig ? getFireflyTourById(discoveryConfig.tourId) : null;

  useEffect(() => {
    modeEnteredAtRef.current = Date.now();
    setDiscoveryDwellReady(false);
    if (!discoveryConfig) return;
    const timer = window.setTimeout(() => setDiscoveryDwellReady(true), discoveryConfig.dwellMs);
    return () => window.clearTimeout(timer);
  }, [mode, discoveryConfig]);

  const dismissDiscoverySession = useCallback((tourId: string) => {
    setDiscoveryDismissedSession((prev) => {
      if (prev.has(tourId)) return prev;
      const next = new Set(prev);
      next.add(tourId);
      return next;
    });
  }, []);

  const handleNavigateFromAskFirefly = useCallback(
    (target: PrimaryMode) => {
      if (target === "adsAssistant") {
        handleOpenAdsAssistant();
        return;
      }
      guardedSetMode(target);
    },
    [guardedSetMode, handleOpenAdsAssistant]
  );

  const tourSession = useFireflyTourSession({
    currentMode: mode,
    onNavigateToMode: handleNavigateFromAskFirefly,
  });

  const handleStartFireflyTour = useCallback(
    (artifact: FireflyTourArtifact, trigger: FireflyTourTrigger = "ask") => {
      setFireflyAskHighlight(null);
      tourSession.startTour(artifact, trigger);
    },
    [tourSession]
  );

  // FF-DOCK-3 — sidebar mini panel starts tours via the same engine.
  useEffect(() => registerFireflyTourStarter(handleStartFireflyTour), [handleStartFireflyTour]);

  const showDiscoveryOffer = Boolean(
    auth &&
      discoveryConfig &&
      discoveryArtifact &&
      discoveryDwellReady &&
      !tourSession.isActive &&
      evaluateFireflyDiscoveryOffer({
        auth,
        currentMode: mode,
        assistLevel,
        discoveryConfig,
        artifact: discoveryArtifact,
        canAccessPrimaryMode,
        hasCapability,
        modeEnteredAtMs: modeEnteredAtRef.current,
        nowMs: Date.now(),
        dismissedPersistently: isFireflyDiscoveryOfferDismissed(discoveryConfig.tourId),
        dismissedThisSession: discoveryDismissedSession.has(discoveryConfig.tourId),
        tourCompleted: isFireflyTourCompleted(discoveryConfig.tourId),
      })
  );

  return {
    fireflyAskHighlight,
    setFireflyAskHighlight,
    assistLevel,
    discoveryConfig,
    discoveryArtifact,
    dismissDiscoverySession,
    tourSession,
    handleStartFireflyTour,
    handleNavigateFromAskFirefly,
    showDiscoveryOffer,
  };
}
